/**
 * Sent drop actions: kill, edit, fetch by unique ID.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import {
  useEditSmswiserDrop,
  useEditSmswiserDropBatch,
  useKillSmswiserDrop,
  useKillSmswiserDropBatch,
  useUpdatePreparedDropFlex,
} from '@/hooks';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import type {
  EditSentDropPayload,
  SentEditPreparedStaticSync,
  SentEditSaveMeta,
} from '@/features/smswiser-campaigns/components/modals/sent/EditSentDropModal.types';
import {
  PREPARED_DROPS_UNDO_KEY,
  priorOf,
  recordRemoteEdit,
} from '@/features/prepared-drops-undo';

export interface UseSentDropActionsParams {
  countryKey: string;
  prepared: { refetch: () => void };
  /** The rows as loaded — read BEFORE the refetch, so an edit's prior values
   *  can be captured. They exist nowhere else (see recordRemoteEdit). */
  drops: PreparedDrop[];
}

/** Prefer the numeric dropId (matches drop-detail kills in audit/SIEM); include
 *  uniqueId when known. `preparedDropId` lets the server mark the row KILLED. */
function toKillPayload(d: PreparedDrop): {
  uniqueId?: string;
  dropId?: number;
  preparedDropId: string;
} {
  if (d.sentDropId != null) {
    return {
      dropId: d.sentDropId,
      ...(d.sentDropUniqueId ? { uniqueId: d.sentDropUniqueId } : {}),
      preparedDropId: d.id,
    };
  }
  return { uniqueId: d.sentDropUniqueId ?? undefined, preparedDropId: d.id };
}

async function syncPreparedStaticTags(
  rows: SentEditPreparedStaticSync[],
  updatePreparedFlex: ReturnType<typeof useUpdatePreparedDropFlex>,
): Promise<void> {
  for (const r of rows) {
    await updatePreparedFlex.mutateAsync({
      countryKey: r.countryKey,
      id: r.preparedId,
      wiserId: r.wiserId,
      payload: { staticTagId: r.staticTagId },
    });
  }
}

/**
 * Record what the edited drops held BEFORE, for the ones that actually changed.
 *
 * Runs before the list refetches, so `drops` is still the pre-edit state — that
 * is the whole reason it can be captured at all.
 */
async function recordEditedPriors(
  results: Array<{ dropId: number; success: boolean }>,
  payloads: Array<{ dropId: number }>,
  drops: PreparedDrop[],
  countryKey: string,
): Promise<void> {
  const changed = new Set(
    results.filter((r) => r.success).map((r) => r.dropId),
  );
  const wanted = new Set(payloads.map((p) => p.dropId).filter((id) => changed.has(id)));
  const priors = drops
    .filter((d) => d.sentDropId != null && wanted.has(d.sentDropId))
    .map(priorOf)
    .filter((p): p is NonNullable<typeof p> => p !== null);
  await recordRemoteEdit({ countryKey, priors });
}

export function useSentDropActions({ countryKey, prepared, drops }: UseSentDropActionsParams) {
  const qc = useQueryClient();
  const { success: toastSuccess, error: toastError, loading: toastLoading, dismiss: toastDismiss } = useToast();
  const killDrop = useKillSmswiserDrop();
  const killDropBatch = useKillSmswiserDropBatch();
  const editDrop = useEditSmswiserDrop();
  const editDropBatch = useEditSmswiserDropBatch();
  const updatePreparedFlex = useUpdatePreparedDropFlex();
  const [killingDropIds, setKillingDropIds] = useState<string[]>([]);

  // Gate the edit modal from data already on the row — no network round-trip.
  // Opening the editor used to await GET /smswiser/drops/by-unique-id per drop
  // (a SMSWiser search + a 200-drop fallback scan), which made bulk edits take
  // minutes to even open the popup. `finishedAt`/`sentStatusLabel` are synced
  // onto the prepared drop, and the PATCH edit still returns 409 for a drop that
  // started/finished after this check — so gating locally stays safe.
  const canEditSentDrop = useCallback(
    async (drop: PreparedDrop): Promise<boolean> => {
      if (drop.finishedAt) {
        toastError('Cannot edit', 'This drop has completed and can no longer be modified.');
        return false;
      }
      const label = (drop.sentStatusLabel ?? '').trim().toLowerCase();
      if (label === 'completed') {
        toastError('Cannot edit', 'This drop has completed and can no longer be modified.');
        return false;
      }
      if (label === 'started') {
        toastError('Cannot edit', 'This drop has already started; edits are not supported.');
        return false;
      }
      return true;
    },
    [toastError],
  );

  // Stop many drops fast: one server-side batched kill (concurrent, idempotent,
  // error-isolated) + one bulk status update for the ones that stopped. No
  // per-drop precheck — an already-finished/stopped drop kills idempotently.
  const handleKillSent = useCallback(
    async (dropsToKill: PreparedDrop[]) => {
      const killable = dropsToKill.filter(
        (d) => d.sentDropId != null || !!d.sentDropUniqueId,
      );
      if (killable.length === 0) {
        toastError('Nothing to stop', 'These drops have no SMSWiser drop to kill.');
        return;
      }
      const loadingId = toastLoading('Killing drops...', `${killable.length} drop(s)`);
      setKillingDropIds(killable.map((d) => d.id));
      try {
        // One batched call: stops concurrently AND marks each row KILLED server-side.
        const res = await killDropBatch.mutateAsync(killable.map(toKillPayload));
        const killedCount = res.results.filter((r) => r.success).length;
        toastDismiss(loadingId);
        const failed = killable.length - killedCount;
        if (failed > 0)
          toastError('Some kills failed', `Stopped ${killedCount} of ${killable.length}; ${failed} failed.`);
        else toastSuccess('Drops stopped', `${killedCount} drop(s) killed.`);
        prepared.refetch();
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Kill failed', getApiErrorMessage(e, 'Could not stop drops.'));
      } finally {
        setKillingDropIds([]);
      }
    },
    [killDropBatch, prepared, toastLoading, toastDismiss, toastSuccess, toastError],
  );

  const handleSaveSentEdit = useCallback(
    async (
      // Shared with the modal so forceSend / shortlink options reach the PATCH typed.
      payload: EditSentDropPayload | EditSentDropPayload[],
      meta?: SentEditSaveMeta,
    ) => {
      const onSuccess = () => prepared.refetch();
      const syncRows = meta?.syncPreparedStaticTags ?? [];
      if (Array.isArray(payload)) {
        if (payload.length === 0 && syncRows.length === 0) return;
        const count = payload.length > 0 ? payload.length : syncRows.length;
        const loadingId = toastLoading(
          'Editing scheduled drops...',
          `${count} drop${count !== 1 ? 's' : ''} — applying changes`,
        );
        try {
          // editBatch runs concurrently + error-isolated, so report per-drop failures.
          const res =
            payload.length > 0 ? await editDropBatch.mutateAsync(payload) : undefined;
          if (syncRows.length > 0) await syncPreparedStaticTags(syncRows, updatePreparedFlex);
          toastDismiss(loadingId);
          // Recorded from what the rows held BEFORE, and only for the drops
          // that actually changed — see recordRemoteEdit for why the client is
          // the only place those values exist.
          await recordEditedPriors(
            res?.results ?? [],
            payload,
            drops,
            countryKey,
          );
          // Without this the Undo button waits for its next poll — the edit is
          // undoable the moment it lands, so it should say so then.
          qc.invalidateQueries({ queryKey: PREPARED_DROPS_UNDO_KEY });
          const failed = (res?.results ?? []).filter((r) => !r.success).length;
          if (failed > 0)
            toastError(
              'Some edits failed',
              `Updated ${payload.length - failed} of ${payload.length}; ${failed} failed.`,
            );
          else toastSuccess('Successfully edited', `${count} drop${count !== 1 ? 's' : ''} updated`);
          onSuccess();
        } catch (err) {
          toastDismiss(loadingId);
          toastError('Edit failed', getApiErrorMessage(err, 'Could not update drops'));
        }
        return;
      }
      // One row from the row's own Edit button. Same capture as the batch above
      // — the modal reaches this branch whenever a single drop is edited, which
      // is the common case, so leaving it unrecorded would mean most sent edits
      // silently had no undo.
      editDrop.mutate(payload, {
        onSuccess: async () => {
          await recordEditedPriors(
            [{ dropId: payload.dropId, success: true }],
            [payload],
            drops,
            countryKey,
          );
          qc.invalidateQueries({ queryKey: PREPARED_DROPS_UNDO_KEY });
          toastSuccess('Successfully edited', 'Drop updated');
          onSuccess();
        },
      });
    },
    [
      countryKey,
      drops,
      editDrop,
      editDropBatch,
      prepared,
      qc,
      toastLoading,
      toastDismiss,
      toastSuccess,
      toastError,
      updatePreparedFlex,
    ],
  );

  return {
    killingDropIds,
    canEditSentDrop,
    handleKillSent,
    handleSaveSentEdit,
    killDrop,
    editDrop,
    editDropBatch,
  };
}
